import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';

import type { ZuubPayer } from '@/@types/zuub/contracts';

interface ConnectionInstructionsProps {
  payer: ZuubPayer;
  startNumber?: number;
}

export const ConnectionInstructions = ({
  payer,
  startNumber = 1,
}: ConnectionInstructionsProps) => {
  const credentialUrl = payer.createCredentialUrl ?? payer.portalUrl;

  if (startNumber === 1) {
    return (
      <div className="vai-mb-6">
        {payer.createCredentialUrl && (
          <p className="vai-mb-4">
            <Trans>
              When creating portal credentials for this payer, please do so at{' '}
              <a
                href={payer.createCredentialUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="vai-text-sapphire-600 hover:vai-underline"
              >
                {payer.createCredentialUrl}
                <FontAwesomeIcon
                  icon={faExternalLink}
                  className="vai-w-3 vai-h-3 vai-ml-1 vai-inline-block vai-align-middle"
                />
              </a>
              . These credentials will be able to be shared across various
              portals.
            </Trans>
          </p>
        )}
        <ol className="vai-space-y-3 vai-list-decimal vai-list-outside vai-pl-6">
          <li>
            <Trans>Sign in to the</Trans>{' '}
            {credentialUrl ? (
              <>
                <a
                  href={credentialUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vai-text-sapphire-600 hover:vai-underline vai-inline-flex vai-items-center vai-gap-1"
                >
                  {payer.name} <Trans>payer portal</Trans>
                  <FontAwesomeIcon
                    icon={faExternalLink}
                    className="vai-w-3 vai-h-3"
                  />
                </a>
                .
              </>
            ) : (
              <>
                {payer.name} <Trans>payer portal</Trans>.
              </>
            )}
          </li>
          <li>
            <Trans>
              Follow portal instructions for creating a new{' '}
              <strong>admin-level</strong> user with the information provided:
            </Trans>
          </li>
        </ol>
      </div>
    );
  }

  return (
    <div className="vai-mt-6">
      <ol
        start={startNumber}
        className="vai-space-y-3 vai-list-decimal vai-list-outside vai-pl-6"
      >
        <li>
          <Trans>
            Check the email address you provided us (usually the office email)
            for confirmation that the new account was created.
          </Trans>
        </li>
        <li>
          <Trans>
            Follow portal instructions to set up the new user, again using the
            information provided above. If the portal requires multi-factor
            authentication, you will receive a code to the email address you
            provided.
          </Trans>
        </li>
        <li>
          <Trans>
            Return to this page and click the &quot;Test Connection&quot; button
            to verify that the verify agent was connected.
          </Trans>
        </li>
      </ol>
    </div>
  );
};
